import { Add, Edit as EditIcon } from "@mui/icons-material";
import { Box, Checkbox, IconButton, Typography } from "@mui/material";

import {
  DataGridPremium,
  DataGridPremiumProps,
  GridColumns,
  GridRenderCellParams,
  GridToolbar,
} from "@mui/x-data-grid-premium";
import {
  axiosInstance,
  Container,
  errorHandler,
  PermissionWrapper,
} from "@neostella/neostella-ui";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { setPreventBack } from "../../../../redux/actions/settings";

interface CategoriesResp {
  UUID: string;
  records: Category[];
  result: string;
}

interface Category {
  category_id: string;
  parent_category: null | string;
  name: string;
  deep: number;
  is_active: boolean;
  created_by: string;
  created_by_name: string;
  updated_by: null | string;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string | null;
}

export default function Categories() {
  const [t] = useTranslation();
  const history = useHistory();
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [queryFilter, setQueryFilter] = useState("enabled");
  const [checked, setChecked] = useState(false);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const getCategories = async () => {
    try {
      setCategories(null);
      const { data: res } = await axiosInstance.get<CategoriesResp>(
        `categories?is_active=${queryFilter}`
      );
      setCategories(res.records);
    } catch (e) {
      errorHandler(e, enqueueSnackbar, t);
    }
  };

  useEffect(() => {
    getCategories();
  }, [checked]);
  useEffect(
    () => () => {
      dispatch(setPreventBack(false));
    },
    []
  );

  const callInactiveCategories = () => {
    if (!checked) {
      setQueryFilter("all");
    } else {
      setQueryFilter("enabled");
    }
  };

  const handleChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
    callInactiveCategories();
  };

  const columns: GridColumns = [
    {
      field: "status",
      headerName: t("general.status"),
      flex: 1,
    },
    {
      field: "created_by",
      headerName: t("general.created_by"),
      flex: 1,
    },
    {
      field: "created_at",
      headerName: t("general.created_at"),
      flex: 1,
      type: "dateTime",
      valueFormatter: (params) =>
        params.value ? format(params.value, "MM/dd/yyyy hh:mm aaaaa'm'") : "",
    },
    {
      field: "actions",
      headerName: t("general.actions"),
      flex: 1,
      renderCell: (params: GridRenderCellParams<string>) => (
        <PermissionWrapper
          permissionKeys={["admin.categories.general.can_update"]}
        >
          <IconButton
            onClick={() => history.push(`/admin/categories/${params.id}`)}
          >
            <EditIcon />
          </IconButton>
        </PermissionWrapper>
      ),
    },
  ];
  const groupingColDef: DataGridPremiumProps["groupingColDef"] = {
    headerName: t("general.name"),
    flex: 2,
  };

  return (
    <Container
      title={t("admin.categories.categories")}
      actions={[
        {
          title: t("admin.categories.create_category"),
          titleIcon: <Add />,
          onClick: () => history.push("/admin/categories/create"),
          permissionKeys: ["admin.categories.general.can_create"],
        },
      ]}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-evenly",
            width: 300,
            m: 4,
          }}
        >
          <Checkbox checked={checked} onChange={handleChangeCheckbox} />
          <Typography>{t("admin.show_inactive_categories")}</Typography>
        </Box>
        <DataGridPremium
          sx={{ minHeight: 400, m: 4 }}
          disableSelectionOnClick
          treeData
          getTreeDataPath={(row) => row.name.split("/")}
          columns={columns}
          rows={
            categories
              ? categories.map((c) => ({
                  id: c.category_id,
                  name: c.name,
                  status: c.is_active
                    ? t("general.active")
                    : t("general.inactive"),
                  created_by: c.created_by_name,
                  created_at: c.created_at ? new Date(c.created_at) : "",
                }))
              : []
          }
          loading={categories === null}
          groupingColDef={groupingColDef}
          components={{ Toolbar: GridToolbar }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
        />
      </Box>
    </Container>
  );
}
